import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import Header from "./Header";
import { useQuestion } from "../../../context/QuestionsContext";
import { useAttemptedQuestions } from "../../../context/AttemptedQuestionsContext";
import QuestionSection from "./QuestionSection";
import QuestionSidebar from "./QuestionSidebar";
import CollapseToggle from "../iit/CollapseToggle";
import useGateTestStyles from "../testStyle/ipmatStyle/gateTestStyle"; // Import the styles
import SubmitSummary from "./SubmitSummary";
import socket from "../../../socket/socket";
import { useQuestionSection } from "../../../hooks/default/useQuestionSection";
import { subjectSubmitApi } from "../../../services/service";
import Loader from "../../../components/Loader";
import { useNavigate } from "react-router-dom";
import ROUTES_NAME from "../../../constants/RoutesName";

function GateTest() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [currentSubject, setCurrentSubject] = useState([0]);
  const [currentSection, setCurrentSection] = useState([]);
  const [subjectId, setSubjectId] = useState(0);
  const [sectionId, setSectionId] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [activeSubjectId, setActiveSubjectId] = useState(null);
  const [sscTimeUp, setSscTimeUp] = useState(
    !!sessionStorage.getItem("submitByH") ||
      !!sessionStorage.getItem("isTimeUp"),
  );
  const [isLastSectionOfLastSubject, setIsLastSectionOfLastSubject] =
    useState(sessionStorage.getItem("submitByH") === "true");
  const [loading, setLoading] = useState(false);
  const [isTimerInitialized, setIsTimerInitialized] = useState(false);
  const saveBeforeSubmitRef = useRef(null);
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const classes = useGateTestStyles(); // Use the styles

  const { attemptedQuestions, fetchAttemptedQuestions, attemptedLoader } =
    useAttemptedQuestions();
  const { question } = useQuestion();

  const subject_submit = sessionStorage.getItem("subject_submit");

  // Flatten all questions from all subjects and sections while preserving section info
  const { questionData, questionToSectionMap, subjectNames, sectionNames } =
    useMemo(() => {
      const questions = [];
      const questionMap = {};

      const subjects = question?.subjects?.map((subject) => ({
        name: subject.subject_name,
        subject_id: subject.id,
      }));

      // setActiveSubjectId(subjects[0]?.subject_id)
      const sections = [];
      let subSections = [];

      question?.subjects?.forEach((subject) => {
        subject?.sections?.forEach((section) => {
          subSections.push({
            name: section.section_name,
            section_id: section.id,
          });
        });
        sections.push(subSections);
        subSections = [];
      });

      // setActiveSectionId(sections[0][0]?.section_id)

      question?.subjects?.forEach((subject) => {
        subject?.sections?.forEach((section) => {
          section?.questions?.forEach((question, qIndex) => {
            questions.push(question);
            questionMap[questions.length - 1] = {
              sectionId: section.id,
              subjectId: subject.id,
            };
          });
        });
      });

      return {
        questionData: questions,
        questionToSectionMap: questionMap,
        subjectNames: subjects,
        sectionNames: sections,
      };
    }, [question]);

  const [isOnSubmit, setIsOnSubmit] = useState(false);

  const subjectLengths = useMemo(
    () =>
      question?.subjects?.map((subject) => subject.subject_question_count) ||
      [],
    [question],
  );

  const sectionLengths = useMemo(() => {
    if (!question?.subjects) return [];

    const result = [];
    let cumulativeSum = 0;

    // First subject gets special treatment with starting 0
    const firstSubject = question.subjects[0];
    if (firstSubject?.sections) {
      const firstSubjectSections = [];
      for (const section of firstSubject.sections) {
        cumulativeSum += section.questions.length;
        firstSubjectSections.push(cumulativeSum);
      }
      result.push(firstSubjectSections);
    }

    // Remaining subjects
    for (let i = 1; i < question.subjects.length; i++) {
      const subject = question.subjects[i];
      const subjectSections = [];

      if (subject?.sections) {
        for (const section of subject.sections) {
          cumulativeSum += section.questions.length;
          subjectSections.push(cumulativeSum);
        }
      }

      result.push(subjectSections);
    }

    return result;
  }, [question]);

  // useEffect(() => {
  //   if (subjectNames?.[0]?.subject_id) {
  //     setActiveSubjectId(subjectNames[0].subject_id);
  //   }
  //   if (sectionNames?.[0]?.[0]?.section_id) {
  //     setActiveSectionId(sectionNames[0][0].section_id);
  //   }
  // }, [subjectNames, sectionNames]);

  const activeSectionQuestions = useMemo(() => {
    return (
      questionData?.filter((q) => {
        return (
          Number(q?.section_id) === Number(activeSectionId) &&
          Number(q?.subject_id) === Number(activeSubjectId)
        );
      }) || []
    );
  }, [questionData, activeSectionId, activeSubjectId]);

  const {
    handleFinalSubmit,
    nextSubjectCounter,
    nextSubject,
    isSubjectTimeUp,
    handleSubjectSubmit,
  } = useQuestionSection({
    questionData: activeSectionQuestions,
    currentQuestion,
    setCurrentQuestion,
    subjectId,
    activeSubjectId,
    setActiveSubjectId,
    subjectNames,
    activeSectionId,
    enableTimer: true,
    saveBeforeSubmitRef,
  });

  useEffect(() => {
    if (
      subject_submit === "yes" &&
      sessionStorage.getItem("subject_timeUp") === "yes"
    ) {
      // window.location.reload();
      // handleFinalSubmit();
      sessionStorage.removeItem("subject_timeUp");
    }
  }, [subject_submit]);

  useEffect(() => {
    if (!subjectNames || subjectNames.length === 0 || attemptedLoader) return;
    const storedSubjects = JSON.parse(
      sessionStorage.getItem("submitted_subjects") || "[]",
    );
    const index = subjectNames?.findIndex(
      (subject) =>
        subject.subject_id === storedSubjects[storedSubjects.length - 1],
    );

    if (subjectNames?.[0]?.subject_id) {
      if (
        storedSubjects.length > 0 &&
        sessionStorage.getItem("subject_wise") === "yes"
      ) {
        setActiveSubjectId(subjectNames[index + 1]?.subject_id);
      } else {
        // submitted_subjects
        const submitted_subjects = JSON.parse(
          sessionStorage.getItem("submitted_subjects") || "[]",
        );

        const index = submitted_subjects.indexOf(activeSubjectId);

        if (index !== -1) {
          // activeSubjectId exists at this index
          setActiveSubjectId(subjectNames[index + 1].subject_id);
        } else {
          // setActiveSubjectId(subjectNames[0].subject_id);
          setActiveSubjectId(
            sessionStorage.getItem("subject_selection") === "yes" &&
              sessionStorage.getItem("userId") !== "4660"
              ? Number(sessionStorage.getItem("selectedSubject"))
              : subjectNames[0].subject_id,
          );
        }
      }
      // result.push(firstSubjectSections);
    }
    if (sectionNames?.[index + 1]?.[0].section_id) {
      // setActiveSectionId(sectionNames[index + 1][0].section_id);
      setActiveSectionId(
        sessionStorage.getItem("subject_selection") === "yes" &&
          sessionStorage.getItem("userId") !== "4660"
          ? Number(sessionStorage.getItem("selectedSection"))
          : sectionNames[index + 1][0].section_id,
      );
    }
    if (
      sessionStorage.getItem("subject_selection") === "yes" &&
      sessionStorage.getItem("userId") !== "4660"
    ) {
      setActiveSubjectId(Number(sessionStorage.getItem("selectedSubject")));
      setActiveSectionId(Number(sessionStorage.getItem("selectedSection")));
    }
    if (
      sessionStorage.getItem("direction") === "unidirectional" &&
      sessionStorage.getItem("userId") !== "4660" &&
      attemptedQuestions?.length > 0
    ) {
      const attemptedQuestionsSectionId = attemptedQuestions[0]?.section_id;
      const attemptedQuestionsSubjectId = attemptedQuestions[0]?.subject_id;
      const attemptedQuestionId = attemptedQuestions[0]?.question_id;

      if (attemptedQuestionsSubjectId) {
        setActiveSubjectId(Number(attemptedQuestionsSubjectId));
      }
      if (attemptedQuestionsSectionId) {
        setActiveSectionId(Number(attemptedQuestionsSectionId));
      }

      let subject = [];
      if (sessionStorage.getItem("subject_selection") === "yes") {
        subject = question?.subjects?.find(
          (subject) =>
            subject.id === Number(sessionStorage.getItem("selectedSubject")),
        );
      } else {
        subject = question?.subjects?.find(
          (subject) => subject.id === Number(attemptedQuestionsSubjectId),
        );
      }

      const section = subject?.sections?.find(
        (section) => section.id === Number(attemptedQuestionsSectionId),
      );

      const sectionQuestionIndex = section?.questions?.findIndex(
        (questionItem) => questionItem?.id === Number(attemptedQuestionId),
      );
  
      if (sessionStorage.getItem("lastAttempted")) {
        setCurrentQuestion(Number(sessionStorage.getItem("lastAttempted")));
        return;
      }
      if (sectionQuestionIndex) {
        setCurrentQuestion(sectionQuestionIndex);
        sessionStorage.setItem("lastAttempted", sectionQuestionIndex);
      }else{
         setCurrentQuestion(0);
        sessionStorage.setItem("lastAttempted", 0);
      }
    }
  }, [subjectNames, sectionNames, attemptedQuestions, attemptedLoader]);

  // useEffect(() => {
  // sessionStorage.setItem("lastAttempted", currentQuestion);
  // }, [currentQuestion]);

  useEffect(() => {
    if (subjectLengths.length > 0) {
      setCurrentSubject((prev) => [...prev, ...subjectLengths]);
    }
  }, [subjectLengths]);

  useEffect(() => {
    if (sectionLengths.length > 0) {
      setCurrentSection((prev) => [...prev, ...sectionLengths]);
    }
  }, [sectionLengths]);

  useEffect(() => {
    if (!questionData.length || !questionToSectionMap[currentQuestion]) return;

    const { sectionId, subjectId } = questionToSectionMap[currentQuestion];
    setSectionId(sectionId);
    setSubjectId(subjectId);
  }, [currentQuestion, questionData, questionToSectionMap]);

  useEffect(() => {
    const storedExamId = sessionStorage.getItem("examId");
    if (storedExamId) {
      fetchAttemptedQuestions(storedExamId);
    }
  }, []);

  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const [reLoadIsOnsubmit, setReLoadIsOnsubmit] = useState(
    sessionStorage.getItem("isOnsubmit"),
  );

  const handleRemove = () => {
    setIsOnSubmit(false);
    setReLoadIsOnsubmit(null);
    setMobileOpen(false);
    sessionStorage.removeItem("isOnsubmit");
        const storedExamId = sessionStorage.getItem("examId");
    if (storedExamId) {
      fetchAttemptedQuestions(storedExamId);
    }
    // Optionally reset other states or session storage items
    setSscTimeUp(false);
    // sessionStorage.removeItem("submitByH");
  };

  useEffect(() => {
    socket.connect();
    const user = sessionStorage.getItem("userId");
    const exam = sessionStorage.getItem("examId");

    const SubmittedByHead = () => {
      sessionStorage.setItem("submitByH", true);
      sessionStorage.setItem("isOnsubmit", true);
      setIsOnSubmit(true);
      setSscTimeUp(true);
    };

    const handleExamSubmit = ({ examId, userId }) => {
      if (userId === user && examId === exam) SubmittedByHead();
    };

    const handleExamSubmitAll = ({ examId }) => {
      if (examId === exam) SubmittedByHead();
    };

    socket.on("exam_submit_check", handleExamSubmit);
    socket.on("exam_submit_check_all", handleExamSubmitAll);

    if (sessionStorage.getItem("submitByH")) {
      SubmittedByHead();
    }

    return () => {
      socket.off("exam_submit_check", handleExamSubmit);
      socket.off("exam_submit_check_all", handleExamSubmitAll);
      socket.disconnect();
    };
  }, []);

  const submit = async () => {
    if (isLastSectionOfLastSubject) return;

    const examId = sessionStorage.getItem("examId");
    setLoading(true);

    // Save current question progress (time + answer) before automatic subject submission
    try {
      if (saveBeforeSubmitRef?.current) {
        await saveBeforeSubmitRef.current();
      }
    } catch (e) {
      console.warn(
        "Could not save question before automatic subject submit:",
        e,
      );
    }

    try {
      const res = await subjectSubmitApi(examId, activeSubjectId);
      if (res.success) {
        setLoading(false);
        nextSubject();
        const submitted_subjects = JSON.parse(
          sessionStorage.getItem("submitted_subjects") || "[]",
        );
        sessionStorage.setItem(
          "submitted_subjects",
          JSON.stringify([...submitted_subjects, activeSubjectId]),
        );
        const subjectCurrentIndex = subjectNames?.findIndex(
          (item) => item.subject_id === activeSubjectId,
        );
        if (sessionStorage.getItem("subject_selection") !== "yes") {
          setActiveSubjectId(subjectNames[subjectCurrentIndex + 1].subject_id);
          setActiveSectionId(
            sectionNames[subjectCurrentIndex + 1][0].section_id,
          );
        }

        setMobileOpen(false); // Close sidebar drawer on mobile/tablet after subject submit
        handleRemove();
        if (
          sessionStorage.getItem("subject_selection") === "yes" &&
          sessionStorage.getItem("userId") !== "4660"
        ) {
          sessionStorage.removeItem("examStarted");
          navigate(ROUTES_NAME.SUBJECT_SELECTION);
          sessionStorage.setItem("subjectSelection", true);
          sessionStorage.removeItem("lastAttempted");
        }

        // navigate(ROUTES_NAME.TEST);
      }
    } catch (error) {
      console.log("error", error);
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   const subjectCurrentIndex = subjectNames?.findIndex(
  //     (item) => item.subject_id === activeSubjectId,
  //   );

  //   if (sessionStorage.getItem("userId") === "4660") {
  //     setIsLastSectionOfLastSubject(true);
  //     return;
  //   }

  //   if (
  //     sessionStorage.getItem("subject_selection") === "yes" &&
  //     sessionStorage.getItem("userId") !== "4660"
  //   ) {
  //     setIsLastSectionOfLastSubject(sessionStorage.getItem("lastSubject"));
  //   } else {
  //     setIsLastSectionOfLastSubject(
  //       subjectCurrentIndex === subjectNames?.length - 1,
  //     );
  //   }

  //   if (
  //     isOnSubmit &&
  //     sessionStorage.getItem("userId") !== "4660" &&
  //     (subjectCurrentIndex !== subjectNames?.length - 1 ||
  //       !sessionStorage.getItem("lastSubject"))
  //   ) {
  //     if (sessionStorage.getItem("submitByH")) {
  //       setIsLastSectionOfLastSubject(true);
  //     } else {
  //       submit();
  //     }
  //   }
  // }, [isOnSubmit]);
  useEffect(() => {
    if (!subjectNames || subjectNames.length === 0) return;

    const subjectCurrentIndex = subjectNames.findIndex(
      (item) => Number(item.subject_id) === Number(activeSubjectId)
    );

    // 🚨 Handle invalid index
    if (subjectCurrentIndex === -1) return;

    const isSpecialUser = sessionStorage.getItem("userId") === "4660";
    const isSubjectSelection =
      sessionStorage.getItem("subject_selection") === "yes";
    const lastSubject = sessionStorage.getItem("lastSubject") === "true";
    const submitByH = sessionStorage.getItem("submitByH") === "true";

    let isLast = false;

    if (submitByH) {
      isLast = true;
    } else if (isSpecialUser) {
      isLast = true;
    } else if (isSubjectSelection) {
      isLast = lastSubject;
    } else {
      isLast = subjectCurrentIndex === subjectNames.length - 1;
    }

    setIsLastSectionOfLastSubject(isLast);

    if (
      isOnSubmit &&
      !isSpecialUser &&
      (subjectCurrentIndex !== subjectNames.length - 1 || !lastSubject)
    ) {
      if (submitByH) {
        setIsLastSectionOfLastSubject(true);
      } else {
        submit();
      }
    }
  }, [isOnSubmit, subjectNames, activeSubjectId, sessionStorage.getItem("submitByH")]);

  return (
    <>
      {loading && (
        <Loader
          message={
            "Your subject will be submitted in a few seconds, please wait..."
          }
        />
      )}
      <Box className={classes.rootContainer}>
        <Box
          style={{
            display:
              (isOnSubmit || reLoadIsOnsubmit) && isLastSectionOfLastSubject
                ? "none"
                : "block",
          }}
        >
          {/* Header + Mobile Menu Icon */}
          <Header activeSubjectId={activeSubjectId}>
            <Box className={classes.headerContainer}>
              {isMobile && (
                <IconButton
                  color="inherit"
                  edge="start"
                  onClick={handleMobileDrawerToggle}
                  className={classes.menuButton}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Header>
        </Box>

        {(isOnSubmit || reLoadIsOnsubmit) && isLastSectionOfLastSubject ? (
          <SubmitSummary
            sectionNames={sectionNames}
            question={question}
            questionData={questionData}
            setIsOnSubmit={setIsOnSubmit}
            isOnSubmit={isOnSubmit}
            sscTimeUp={sscTimeUp}
            handleRemove={handleRemove}
            activeSubjectId={activeSubjectId}
            nextSubject={nextSubject}
            subjectNames={subjectNames}
            setActiveSubjectId={setActiveSubjectId}
            setActiveSectionId={setActiveSectionId}
            saveBeforeSubmitRef={saveBeforeSubmitRef}
            handleSubjectSubmit={handleSubjectSubmit}
            currentQuestionId={
              questionData?.[currentQuestion]?.id || null
            }
          />
        ) : (
          <Box className={classes.mainContainer}>
            {/* Main content */}
            <Box className={classes.contentContainer}>
              <Box className={classes.questionContainer}>
                <QuestionSection
                  isMobile={isMobile}
                  setSscTimeUp={setSscTimeUp}
                  setIsOnSubmit={setIsOnSubmit}
                  saveBeforeSubmitRef={saveBeforeSubmitRef}
                  subjectNames={subjectNames}
                  sectionNames={sectionNames}
                  currentSection={currentSection}
                  questionData={questionData}
                  currentQuestion={currentQuestion}
                  setCurrentQuestion={setCurrentQuestion}
                  subjectId={subjectId}
                  sectionId={sectionId}
                  currentSubject={currentSubject}
                  setActiveSubjectId={setActiveSubjectId}
                  setActiveSectionId={setActiveSectionId}
                  activeSectionId={activeSectionId}
                  activeSubjectId={activeSubjectId}
                  desktopOpen={desktopOpen}
                  onTimerInitialized={setIsTimerInitialized}
                  isTimerInitialized={isTimerInitialized}
                />
              </Box>

              {/* Sidebar wrapper: pull-tab + panel (desktop only) */}
              <Box className={classes.sidebarWrapper}>
                {/* Pull-tab toggle: always visible */}
                <Box
                  className={classes.toggleBox}
                  onClick={handleDesktopToggle}
                >
                  <CollapseToggle
                    open={desktopOpen}
                    setOpen={handleDesktopToggle}
                  />
                </Box>

                {/* Sidebar panel: slides in/out */}
                <Box
                  className={classes.sidebarContainer}
                  sx={{
                    width: desktopOpen ? "300px" : "0px",
                    overflow: "hidden",
                    transition: "width 0.3s ease",
                  }}
                >
                  <QuestionSidebar
                    questionData={questionData}
                    currentQuestion={currentQuestion}
                    setCurrentQuestion={setCurrentQuestion}
                    sectionId={sectionId}
                    subjectId={subjectId}
                    activeSectionId={activeSectionId}
                    activeSubjectId={activeSubjectId}
                    setIsOnSubmit={setIsOnSubmit}
                    subjectNames={subjectNames}
                    saveBeforeSubmitRef={saveBeforeSubmitRef}
                  />
                </Box>
              </Box>

              {/* Sidebar Drawer (mobile only) */}
            </Box>

            <Drawer
              variant="temporary"
              anchor="right"
              open={mobileOpen}
              onClose={handleMobileDrawerToggle}
              ModalProps={{ keepMounted: true }}
              className={classes.mobileDrawer}
            >
              <QuestionSidebar
                questionData={questionData}
                currentQuestion={currentQuestion}
                setCurrentQuestion={setCurrentQuestion}
                sectionId={sectionId}
                subjectId={subjectId}
                activeSectionId={activeSectionId}
                activeSubjectId={activeSubjectId}
                setIsOnSubmit={setIsOnSubmit}
                mobileOpen={mobileOpen}
                isMobile={isMobile}
                subjectNames={subjectNames}
                onClose={handleMobileDrawerToggle}
                saveBeforeSubmitRef={saveBeforeSubmitRef}
              />
            </Drawer>
          </Box>
        )}
      </Box>
    </>
  );
}

export default GateTest;
